import './styles/BookingModal.scss';

import { Grid } from '@mui/material';
import { Booking, UpdateBooking } from '@thro-types/leihbar';

import { Formik } from 'formik';
import { pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useBookingMutation } from 'api/hooks/leihbar';
import { Modal } from 'app/components';
import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import { BookingInformation } from './components/BookingInformation';
import { bookingValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
  booking: Booking | undefined;
  open: boolean;
}

export function BookingEditModal(props: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { update } = useBookingMutation();

  const initialValues = () => {
    return props.booking
      ? {
          ...pick(props.booking, ['id', 'startTime', 'article', 'endTime'])
        }
      : { startTime: new Date(), endTime: new Date() };
  };

  const handleSaveClick = async (
    values: Pick<Booking, 'startTime' | 'endTime' | 'article'>,
    id: number | undefined
  ) => {
    if (submitting || !id) {
      return;
    }
    setSubmitting(true);
    setDisabledCancel(true);
    try {
      const booking: UpdateBooking = {
        id: id,
        startTime: new Date(values.startTime),
        endTime: new Date(values.endTime),
        articleId: values.article?.id
      };
      await update.mutateAsync({ id: id, data: booking });

      dispatch(
        AlertActions.success(t(translations.pages.leihbar.booking.alerts.successfulUpdate()))
      );
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.leihbar.booking.alerts.failedUpdate())));
      setSubmitting(false);
    }
    setDisabledCancel(false);
    setSubmitting(false);
    props.closeHandler();
  };

  const steps = ['Information'];

  if (!props.booking) {
    return null;
  }

  const SettingsModalStep = ({ step }) => {
    if (!props.booking?.article?.product) {
      return null;
    }

    switch (step) {
      case 0:
        return (
          <BookingInformation
            setDisabled={setIsDisabled}
            isDisabled={isDisabled}
            booking={props.booking}
            productId={props.booking.article?.product?.id}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={() => props.closeHandler()}
      headline={t(translations.pages.leihbar.booking.editModalTitle())}
      disabledCancel={disabledCancel}
      width="half"
    >
      <Grid item className="bookingModal">
        <Formik
          initialValues={initialValues()}
          onSubmit={values => handleSaveClick(values, props.booking?.id)}
          validationSchema={bookingValidationSchema(t)}
          validate={values => {
            return validateForm(values, bookingValidationSchema(t), dispatch);
          }}
        >
          <MultistepForm
            submitting={submitting}
            steps={steps}
            onCancel={props.closeHandler}
            hideStepper
            isDisabled={isDisabled}
          >
            {activeStep => (
              <Grid container className="bookingModal__body">
                <SettingsModalStep step={activeStep} />
              </Grid>
            )}
          </MultistepForm>
        </Formik>
      </Grid>
    </Modal>
  );
}
